import { Subject } from 'rxjs';
import { MeetingTrackType } from '../../interfaces/meeting-service.interface';
import { JitsiEventMapper } from './event-mapper';
import { JitsiTrackEvents } from 'nexgen-types/lib-jitsi';
import { AudioMixerEffect } from './audio-mixer';
import { getSelfUserPariticipant } from '../../../host/stores';
import { getResolutionTierFromScreen, ScreenResolutionTier } from './screen-resolution-detector';

declare let JitsiMeetJS: any;

export class ScreenShareManager {
  _screenShareTracks: any[] = [];

  constructor(
    private eventSubject: Subject<any>,
    private eventMapper: JitsiEventMapper,
    private getTrackTypeFromJitsiTrack: (track: any) => MeetingTrackType,
    private localTracks: any[]
  ) {}

  async startScreenShare(conference: any, onStopped?: () => void): Promise<void> {
    try {
      this.checkConferenceExists(conference);
      this.checkScreenSharingSupport();
      this.checkExistingScreenShare();

      const screenTracks = await this.requestScreenSharingPermission();
      await this.addScreenTrackToConference(screenTracks, conference, onStopped);
      this._screenShareTracks = [...screenTracks];
    } catch (error) {
      console.error('Screen sharing failed:', error);
      this.eventSubject.next(this.eventMapper.createTrackError(MeetingTrackType.SCREEN, error));
      throw error;
    }
  }

  async stopScreenShare(conference: any): Promise<void> {
    try {
      this.checkConferenceExists(conference);

      const screenTrack = this.findScreenTrack();
      if (!screenTrack) {
        return;
      }
      await this.removeScreenTrackFromConference(screenTrack, conference);
    } catch (error) {
      console.error('Error stopping screen share:', error);
      this.eventSubject.next(this.eventMapper.createTrackError(MeetingTrackType.SCREEN, error));
      throw error;
    }
  }

  async toggleScreenShare(conference: any): Promise<void> {
    const hasScreenShare = this.findScreenTrack() !== undefined;
    if (hasScreenShare) {
      await this.stopScreenShare(conference);
    } else {
      await this.startScreenShare(conference);
    }
  }

  private checkConferenceExists(conference: any): void {
    if (!conference) {
      throw new Error('Conference not joined.');
    }
  }

  private checkScreenSharingSupport(): void {
    if (!JitsiMeetJS.isDesktopSharingEnabled()) {
      throw new Error('Screen sharing is not supported in this browser');
    }
  }

  private checkExistingScreenShare(): void {
    const existingScreenTrack = this.findScreenTrack();
    if (existingScreenTrack) {
      throw new Error('Screen sharing is already active');
    }
  }

  private findScreenTrack(): any {
    return this.localTracks.find(
      (track) => this.getTrackTypeFromJitsiTrack(track) === MeetingTrackType.SCREEN
    );
  }

  private async requestScreenSharingPermission(): Promise<any[]> {
    const tier = getResolutionTierFromScreen();
    console.log(
      `[ScreenShareManager] Detected screen tier: ${tier.label} (${tier.width}x${tier.height})`
    );

    const options = {
      devices: ['desktop'],
      desktopSharingSources: ['screen', 'window', 'tab', 'audio'],
      // Very low initial frame rate for fastest permission grant and start-up
      desktopSharingFrameRate: { min: 1, max: 5 },
      constraints: {
        video: {
          frameRate: { ideal: 3, max: 5 },
          width: { ideal: tier.width, max: tier.width },
          height: { ideal: tier.height, max: tier.height },
        },
      },
    };

    try {
      const tracks = await JitsiMeetJS.createLocalTracks(options);

      // Upgrade to full resolution and frame rate after screen share is established
      setTimeout(() => {
        void this.upgradeScreenShareQuality(tracks, tier);
      }, 2000);

      return tracks;
    } catch (error) {
      this.handleScreenSharingPermissionError(error);
      throw error;
    }
  }

  private async upgradeScreenShareQuality(
    tracks: any[],
    tier: ScreenResolutionTier
  ): Promise<void> {
    try {
      for (const track of tracks) {
        if (track.getType() === 'video') {
          const nativeTrack = track.getTrack();
          if (nativeTrack && typeof nativeTrack.applyConstraints === 'function') {
            await nativeTrack.applyConstraints({
              frameRate: tier.frameRate,
              width: { ideal: tier.width, max: tier.width },
              height: { ideal: tier.height, max: tier.height },
            });
            console.log(
              `[ScreenShareManager] Screen share upgraded to ${tier.label} — ${tier.width}x${tier.height} @ ${tier.frameRate.ideal}fps`
            );
          }
        }
      }
    } catch (error) {
      console.warn('[ScreenShareManager] Failed to upgrade screen share quality:', error);
    }
  }

  private async addScreenTrackToConference(
    screenTracks: any[],
    conference: any,
    onStopped?: () => void
  ): Promise<void> {
    if (!screenTracks || screenTracks.length === 0) {
      throw new Error('Failed to create screen sharing tracks');
    }

    for (const track of screenTracks) {
      if (track.type === 'audio') {
        // Tab/desktop audio is mixed INTO the local mic track via a WebAudio effect; it
        // is NOT a standalone conference track. It must therefore NOT be added to
        // `localTracks` (== localTrackService.tracks) — otherwise the local collection
        // holds two 'audio' tracks and the mic mute/unmute + getLocalMuteState logic
        // (which all do `find(t => t.getType() === 'audio')`) operate on the wrong track,
        // so the microphone stops transmitting once a tab is shared with audio.
        const micTrack = getSelfUserPariticipant()?.audioTrack?.track;
        if (micTrack) {
          try {
            await micTrack.setEffect(new AudioMixerEffect(track));
          } catch (error) {
            console.warn('[ScreenShareManager] Failed to mix tab audio into mic:', error);
          }
        } else {
          // No mic to mix into (Jitsi allows a single audio track per participant), so
          // the shared tab audio can't be transmitted — drop it rather than break audio.
          console.warn('[ScreenShareManager] No local mic track; tab audio will not be shared');
          try {
            track.dispose?.();
          } catch {
            /* noop */
          }
        }
      } else {
        this.localTracks.push(track);
        await conference.addTrack(track);
        this.setupScreenTrackEventListeners(track, conference, onStopped);
      }

      // NEX-817: Fix - pass correct trackType instead of participantId
      const trackType = this.getTrackTypeFromJitsiTrack(track);
      this.eventSubject.next(this.eventMapper.createTrackAdded(track, trackType));
    }
  }

  private setupScreenTrackEventListeners(
    screenTrack: any,
    conference: any,
    onStopped?: () => void
  ): void {
    screenTrack.addEventListener(JitsiTrackEvents.LOCAL_TRACK_STOPPED, async () => {
      await this.removeScreenTrackFromConference(screenTrack, conference);
      onStopped?.();
    });
  }

  /**
   * Tear the share down step by step, isolating every step.
   *
   * These used to share one try/catch, which made the teardown all-or-nothing: `mute()` on a
   * local desktop track already detaches the stream, so the `removeTrack()` right after it
   * can reject — and everything below it (dispose, stopping the raw MediaStreamTracks,
   * clearing `_screenShareTracks`, removing the track from `localTracks`) was skipped
   * silently. That left the share half torn down: `findScreenTrack()` still reported an
   * active share, the OS capture indicator stayed on, and the source could still be
   * advertised to anyone joining afterwards — which is what put a dead screen-share tile on
   * a viewer who refreshed after the share had ended.
   *
   * Every step now runs regardless of the ones before it failing.
   */
  private async removeScreenTrackFromConference(screenTrack: any, conference: any): Promise<void> {
    // Remove the tab-audio mixer effect from the mic so the microphone goes back to
    // transmitting normally once the share ends.
    try {
      await getSelfUserPariticipant()?.audioTrack?.track?.setEffect(undefined);
    } catch (effectError) {
      console.warn('[ScreenShareManager] Failed to clear tab-audio mic effect:', effectError);
    }

    try {
      await screenTrack.mute();
    } catch (muteError) {
      console.warn('[ScreenShareManager] Failed to mute screen track:', muteError);
    }

    try {
      await conference.removeTrack(screenTrack);
    } catch (removeError) {
      // Expected when mute() already detached the stream; the track still has to go.
      console.warn(
        '[ScreenShareManager] Failed to remove screen track from conference:',
        removeError
      );
    }

    try {
      await screenTrack.dispose();
    } catch (disposeError) {
      console.warn('[ScreenShareManager] Failed to dispose screen track:', disposeError);
    }

    // Stop the raw capture so the browser/OS "sharing" indicator actually goes away.
    for (const track of this._screenShareTracks) {
      try {
        if (track?.track && typeof track.track.stop === 'function') track.track.stop();
        if (typeof track?.getTrack === 'function') track.getTrack()?.stop?.();
      } catch (stopError) {
        console.warn('[ScreenShareManager] Failed to stop a raw screen track:', stopError);
      }
    }
    this._screenShareTracks = [];

    // Unconditional: this array is what findScreenTrack() — and therefore
    // "am I still sharing?" — is answered from.
    const index = this.localTracks.indexOf(screenTrack);
    if (index !== -1) this.localTracks.splice(index, 1);

    await new Promise((resolve) => setTimeout(resolve, 1000));
  }

  private handleScreenSharingPermissionError(error: any): void {
    if (
      error.name === 'gum.permission_denied' ||
      error.message?.includes('permission denied') ||
      error.name === 'NotAllowedError'
    ) {
      console.error('[ScreenShareManager] Screen sharing permission was denied by the user');
    } else if (
      error.name === 'gum.constraint_failed' ||
      error.message?.includes('constraint_failed')
    ) {
      console.error('[ScreenShareManager] Screen sharing constraints could not be satisfied');
    } else if (
      error.name === 'gum.unavailable_screen_capturing' ||
      error.message?.includes('unavailable')
    ) {
      console.error('[ScreenShareManager] Screen sharing is not available in this environment');
    } else {
      console.error('[ScreenShareManager] Screen sharing error:', error.message || error);
    }
  }
}
